import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@components/layout/Navbar";
import FormStepper from "@components/form-builder/FormStepper";
import SuccessModal from "@components/common/SuccessModal";
import FailureModal from "@components/common/FailureModal";
import StepHeader from "@components/form-builder/StepHeader";
import { useForm } from "@context/FormContext";
import SortableQuestionList from "@components/form-builder/SortableQuestionList";
import { saveTemplate } from "@services/template";
import { sanitizeFormValues } from "@utils/sanitize";

export default function FormReview() {
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { formData, tabs, setTabs } = useForm();
  const [activeTabId, setActiveTabId] = useState("tab1");

  const handleSave = async () => {
    const cleanTabs = tabs.map((tab) => ({
      ...tab,
      content: tab.content.map(({ icon, ...rest }) => rest),
    }));
    const payload = {
      id: formData.id || null,
      name: formData.formName,
      signatures_required_by: formData.signatureBy,
      fillable_at_home: formData.canFillAtHome,
      content: cleanTabs,
    };

    try {
      const response = await saveTemplate(sanitizeFormValues(payload));
      if (response) {
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          navigate("/", { state: { defaultTab: "Form Library" } });
        }, 3000);
      }
    } catch (error) {
      console.error("Patient save failed", error);
      setErrorMessage(error.message || "Something went wrong.");
      setShowFailure(true);
      setTimeout(() => setShowFailure(false), 4000);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="sticky top-0 z-50">
        <Navbar showFieldSearch={false} />
      </div>
      <div className="mt-4 px-4">
        <div className="flex flex-col px-5 py-5 min-h-[calc(100vh-4rem)]">
          <StepHeader title="Create New Form" onClose={() => navigate("/")} />
          <FormStepper currentStep={3} />

          <div className="flex-grow overflow-y-auto">
            <div className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-400">
              Basic Information
            </div>
            <div className="grid grid-cols-1 mb-4 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
              <div>
                <div className="font-bold">Form Name</div>
                <div>{formData.formName}</div>
              </div>
              <div>
                <div className="font-bold">
                  Can be filled by patient at home?
                </div>
                <div>{formData.canFillAtHome ? "Yes" : "No"}</div>
              </div>
            </div>

            <hr className="my-4" />
            <div className="mb-4">
              <div className="text-xl font-bold text-gray-900 dark:text-gray-400 mb-4">
                Form Questions
              </div>
              {/* Tab navigation */}
              <div className="flex items-center gap-2 mb-4">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTabId(tab.id)}
                    className={`px-8 py-2 rounded-full text-sm font-medium transition-colors ${
                      tab.id === activeTabId
                        ? "bg-gray-900 text-white"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <SortableQuestionList
                editable={true}
                questions={
                  tabs.find((tab) => tab.id === activeTabId)?.content || []
                }
                setQuestions={(newContent) => {
                  const updatedTabs = tabs.map((tab) =>
                    tab.id === activeTabId
                      ? { ...tab, content: newContent }
                      : tab
                  );
                  setTabs(updatedTabs);
                }}
              />
            </div>
          </div>

          <div className="pt-4">
            <button
              onClick={handleSave}
              className="w-full text-base font-medium bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg"
            >
              Save and Create Form
            </button>
          </div>
        </div>
      </div>
      <SuccessModal
        isOpen={showSuccess}
        title={"Form created successfully"}
        message={"Your custom form is now saved and ready to use."}
      />
      <FailureModal
        isOpen={showFailure}
        titleError={"Failed to create form"}
        errorMessage={errorMessage}
      />
    </div>
  );
}
